import { Col, InputNumber, Radio, Row, Space } from "antd";
import dayjs from "dayjs";
import { t } from "i18next";
import React, { useContext, useEffect, useState } from "react";

import { RadioChangeEvent } from "antd/lib";
import { DatePicker } from "../../../../../components/DatePicker/DatePicker";
import { Input } from "../../../../../components/Input/Input";
import { MandatoryField } from "../../../../../components/MandatoryField/MandatoryField";
import { Select } from "../../../../../components/Select/Select";
import { DATE_FORMAT } from "../../../../../core/constants";
import { IDozvolaInformacijeEditProps } from "../../../../../modules/ministry/ministry.types";
import { ExciseLicencesContext } from "../../../../../modules/obligee/exciseLicences/exciseLicences.context";
import { exciseLicencesService } from "../../../../../modules/obligee/exciseLicences/exciseLicences.service";
import {
  DozvolaTipObjekta,
  IDozvolaInformacijeProps,
  IDozvolaInformacijeSkladistaProps,
  IUserInformationDozvolaProps,
} from "../../../../../modules/obligee/exciseLicences/exciseLicences.types";
import { AkciznaMarkicaKategorijaProzivoda } from "../../../../../modules/obligee/exciseStamps/exciseStamps.types";
import { clearNonNumericValues } from "../../../../../utils/functions";
import style from "./TopPart.module.scss";

interface ITopPartProps {
  tipDozvole: DozvolaTipObjekta | null;
  data: IUserInformationDozvolaProps;
  filledData: IDozvolaInformacijeSkladistaProps;
  stateValues: IDozvolaInformacijeEditProps;
  licenceRequest: number | null;
  setInformacijeDozvole: (
    updatedState: Partial<IDozvolaInformacijeProps>
  ) => void;
  setKategorijaProizvodaId: (id: AkciznaMarkicaKategorijaProzivoda) => void;
  kategorijaProizvodaId: AkciznaMarkicaKategorijaProzivoda | null;
}

export const TopPart: React.FC<ITopPartProps> = ({
  data,
  filledData,
  licenceRequest,
  tipDozvole,
  setInformacijeDozvole,
  setKategorijaProizvodaId,
  kategorijaProizvodaId,
  stateValues,
}) => {
  const [tipObjekta, setTipObjekta] = useState<DozvolaTipObjekta | null>(
    DozvolaTipObjekta.SKLADISTE
  );

  const options = exciseLicencesService.getAkcizniProizvodiOptions(
    data.akcizniProizvod
  );
  const { setExciseLicences } = useContext(ExciseLicencesContext);

  const oilInput = () => {
    if (
      kategorijaProizvodaId === AkciznaMarkicaKategorijaProzivoda.DERIVATI_NAFTE
    )
      return (
        <Row className={style.row}>
          <Col span={5}></Col>
          <Col span={5}></Col>
          <Col span={5} className="alignRight mt-5">
            <span className={style.fontSize}>
              {MandatoryField("AKCIZNE_DOZVOLE.TARIFNA_OZNAKA")}:
            </span>
          </Col>
          <Col span={5}>
            <Input
              name="tarifna-oznaka"
              className={style.fontSize}
              onChange={(e) =>
                setInformacijeDozvole({ tarifnaOznaka: e.target.value })
              }
            ></Input>
          </Col>
        </Row>
      );
    return null;
  };

  let queryParams = new URLSearchParams(window.location.search);
  const id = queryParams.get("id");

  const isDisabled = () => {
    if (licenceRequest === 2 || licenceRequest === 3 || !id) return false;
    if (filledData?.kategorijaProizvodaId === 0) return true;
    return Boolean(filledData?.kategorijaProizvodaId);
  };

  const isMandatoryIfDisabled = (label: string) => {
    if (isDisabled() === false) return MandatoryField(label);
    if (!id) return MandatoryField(label);
    return t(label);
  };

  const handleOnChange = (val: number) => {
    setKategorijaProizvodaId(val);
    setInformacijeDozvole({ kategorijaProizvodaId: val });
  };

  const handleRadio = (event: RadioChangeEvent) => {
    setTipObjekta(event.target.value);
    setExciseLicences({ tipDozvole: event.target.value });
  };

  useEffect(() => {
    if (!tipObjekta && !!tipDozvole) {
      setTipObjekta(tipDozvole);
    }
  }, [tipDozvole]);

  return (
    <Col span={24} className={style.mainContainer}>
      <Row className={style.row}>
        <Col span={5} className="alignRight">
          <span className={style.fontSize}>
            {t("AKCIZNE_DOZVOLE.TIP_OBJEKTA")}:
          </span>
        </Col>
        <Col span={18}>
          <Radio.Group
            disabled={isDisabled()}
            onChange={handleRadio}
            value={tipObjekta}
          >
            <Radio value={DozvolaTipObjekta.SKLADISTE}>
              {t("SETTINGS_OBJECTS.AKCIZNO_SKLADISTE")}
            </Radio>
            <Radio value={DozvolaTipObjekta.PROIZVODNJA_SA_SKLADISTEM}>
              {t("AKCIZNE_DOZVOLE.AKCIZNO_SKLADISTE_PROIZVODNI_POGON")}
            </Radio>
          </Radio.Group>
        </Col>
      </Row>
      <Row className={style.row}>
        <Col span={5} className="alignRight">
          <span className={style.fontSize}>
            {isMandatoryIfDisabled(
              "AKCIZNE_DOZVOLE.VRSTA_AKCIZNOG_PROIZVODA_KOJI_CE_SE_PROIZVODITI_I_SMESTATI_U_SKLADISTE"
            )}
            :
          </span>
        </Col>
        <Col span={5}>
          <Select
            onChange={handleOnChange}
            disabled={isDisabled()}
            className={style.fontSize}
            value={kategorijaProizvodaId}
            name="vrsta-akciznog-proizvoda"
            placeholder={t(
              "AKCIZNE_DOZVOLE.IZABERITE_VRSTU_AKCIZNOG_PROIZVODA"
            )}
            options={options || []}
          />
        </Col>
        {kategorijaProizvodaId !== AkciznaMarkicaKategorijaProzivoda.DUVAN ? (
          <>
            <Col span={5} className="alignRight pb-15">
              <span className={style.fontSize}>
                {tipObjekta === DozvolaTipObjekta.SKLADISTE
                  ? isMandatoryIfDisabled(
                      "AKCIZNE_DOZVOLE.KOLICINA_USKLADISTENIH_PROIZVODA_NA_ZALIHAMA_U_PROSTORU"
                    )
                  : t(
                      "AKCIZNE_DOZVOLE.KOLICINA_USKLADISTENIH_PROIZVODA_NA_ZALIHAMA_U_PROSTORU"
                    )}
                :
              </span>
            </Col>
            <Col span={5}>
              <InputNumber
                min={0}
                disabled={isDisabled()}
                className={`${style.fontSize} w-100`}
                onChange={(value) =>
                  setInformacijeDozvole({
                    kolicinaUskladistenihProizvoda: value,
                  })
                }
                value={stateValues.kolicinaUskladistenihProizvoda}
                name="kolicina-uskladistenih-proizvoda-na-zalihama"
                onKeyDown={clearNonNumericValues}
              />
            </Col>
            <Col span={5}></Col>
            <Col span={5}></Col>
            <Col span={5} className="alignRight pt-6">
              <span className={style.fontSize}>
                {tipObjekta === DozvolaTipObjekta.SKLADISTE
                  ? isMandatoryIfDisabled(
                      "AKCIZNE_DOZVOLE.PROSECAN_MESECNI_RASHOD"
                    )
                  : t("AKCIZNE_DOZVOLE.PROSECAN_MESECNI_RASHOD")}
                :
              </span>
            </Col>
            <Col span={5} className="pt-6">
              <InputNumber
                min={0}
                disabled={isDisabled()}
                className={`${style.fontSize} w-100`}
                onChange={(value) =>
                  setInformacijeDozvole({
                    prosecanMesecniRashod: value,
                  })
                }
                value={stateValues.prosecanMesecniRashod}
                name="prosecan-mesecni-rashod"
              />
            </Col>
          </>
        ) : (
          <>
            <Col span={5} className="alignRight">
              <span className={style.fontSize}>
                {tipObjekta === DozvolaTipObjekta.SKLADISTE
                  ? isMandatoryIfDisabled(
                      "AKCIZNE_DOZVOLE.KOLICINA_USKLADISTENIH_PROIZVODA_NA_ZALIHAMA_U_PROSTORU"
                    )
                  : t(
                      "AKCIZNE_DOZVOLE.KOLICINA_USKLADISTENIH_PROIZVODA_NA_ZALIHAMA_U_PROSTORU"
                    )}
                :
              </span>
            </Col>
            <Col span={5} className="pb-15">
              <Space.Compact style={{ width: "100%" }}>
                <InputNumber
                  onChange={(value) =>
                    setInformacijeDozvole({
                      kolicinaDuvanaKg: value,
                    })
                  }
                  min={0}
                  placeholder={"kg"}
                  className={`${style.fontSize} w-40`}
                  disabled={isDisabled()}
                  step={0.1}
                  value={stateValues.kolicinaDuvanaKg}
                  name="uskladistenih-kolicina-duvana-kg"
                  onKeyDown={(event) => clearNonNumericValues(event, ["."])}
                />
                <InputNumber
                  min={0}
                  disabled={isDisabled()}
                  className={`${style.fontSize} w-60`}
                  placeholder={t("PLACEHOLDERS.KOMADI")}
                  onChange={(value) =>
                    setInformacijeDozvole({
                      kolicinaPojedinacnihPakovanjaKomadi: value,
                    })
                  }
                  value={stateValues.kolicinaPojedinacnihPakovanjaKomadi}
                  name="uskladistenih-kolicina-pojedinacnih-pakovanja-komadi"
                  onKeyDown={clearNonNumericValues}
                />
              </Space.Compact>
            </Col>
            <Col span={5}></Col>
            <Col span={5}></Col>
            <Col span={5} className="alignRight pt-6">
              <span className={style.fontSize}>
                {tipObjekta === DozvolaTipObjekta.SKLADISTE
                  ? isMandatoryIfDisabled(
                      "AKCIZNE_DOZVOLE.PROSECAN_MESECNI_RASHOD"
                    )
                  : t("AKCIZNE_DOZVOLE.PROSECAN_MESECNI_RASHOD")}
                :
              </span>
            </Col>
            <Col span={5}>
              <Space.Compact style={{ width: "100%" }}>
                <InputNumber
                  onChange={(value) =>
                    setInformacijeDozvole({
                      prosecnaKolicinaDuvanaKg: value,
                    })
                  }
                  value={stateValues.prosecnaKolicinaDuvanaKg}
                  placeholder={"kg"}
                  className={`${style.fontSize} w-40`}
                  disabled={isDisabled()}
                  step={0.1}
                  name="prosecna-kolicina-duvana-kg"
                  onKeyDown={(event) => clearNonNumericValues(event, ["."])}
                />
                <InputNumber
                  onChange={(value) =>
                    setInformacijeDozvole({
                      prosecnaKolicinaPojedinacnihPakovanjaKomadi: value,
                    })
                  }
                  value={
                    stateValues.prosecnaKolicinaPojedinacnihPakovanjaKomadi
                  }
                  placeholder={t("PLACEHOLDERS.KOMADI")}
                  className={`${style.fontSize} w-60`}
                  disabled={isDisabled()}
                  name="prosecna-kolicina-pojedinacnih-pakovanja-komadi"
                />
              </Space.Compact>
            </Col>
          </>
        )}
      </Row>
      <Row className={style.row}>
        <Col span={2}></Col>
        <Col span={8} className={style.subtitle}>
          <span className={style.fontSize}>
            {t(
              "AKCIZNE_DOZVOLE.ADRESA_GDE_SE_OBAVLJA_PROIZVODNJA_AKCIZNOG_PROIZVODA"
            )}
          </span>
        </Col>
        <Col span={5} className="alignRight pt-6">
          <span className={style.fontSize}>
            {isMandatoryIfDisabled(
              "AKCIZNE_DOZVOLE.NACIN_OTPREMANJA_AKCIZNIH_PROIZVODA"
            )}
            :
          </span>
        </Col>
        <Col span={5}>
          <Input
            onChange={(e) =>
              setInformacijeDozvole({
                nacinOtpremanja: e.target.value,
              })
            }
            value={stateValues.nacinOtpremanja}
            className={style.fontSize}
            disabled={isDisabled()}
            maxLength={100}
            name="nacin-otpremanja-akciznih-proizvoda"
          />
        </Col>
      </Row>
      <Row className={style.row}>
        <Col span={5} className="alignRight pt-6">
          <span className={style.fontSize}>
            {isMandatoryIfDisabled("AKCIZNE_DOZVOLE.ULICA_I_BROJ")}:
          </span>
        </Col>
        <Col span={5}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              onChange={(e) =>
                setInformacijeDozvole({
                  ulicaProizvodnje: e.target.value,
                })
              }
              value={stateValues.ulicaProizvodnje}
              placeholder={t("PLACEHOLDERS.ULICA")}
              className={`${style.fontSize} w-75`}
              disabled={isDisabled()}
              name="ulica"
            />
            <Input
              onChange={(e) =>
                setInformacijeDozvole({
                  brojUliceProizvodnje: e.target.value,
                })
              }
              value={stateValues.brojUliceProizvodnje}
              placeholder={t("PLACEHOLDERS.BROJ")}
              className={`${style.fontSize} w-25`}
              disabled={isDisabled()}
              name="broj"
            />
          </Space.Compact>
        </Col>
        <Col span={5} className="alignRight pt-6">
          <span className={style.fontSize}>
            {isMandatoryIfDisabled(
              "AKCIZNE_DOZVOLE.PODATAK_O_MERNIM_UREDJAJIMA"
            )}
            :
          </span>
        </Col>
        <Col span={5}>
          <Input
            onChange={(e) =>
              setInformacijeDozvole({
                podatakOMernimUredjajima: e.target.value,
              })
            }
            value={stateValues.podatakOMernimUredjajima}
            className={style.fontSize}
            disabled={isDisabled()}
            name="podatak-merni-uredjaji"
          />
        </Col>
      </Row>
      <Row className={style.row}>
        <Col span={5} className="alignRight pt-6">
          <span className={style.fontSize}>
            {isMandatoryIfDisabled("AKCIZNE_DOZVOLE.POSTANSKI_BROJ")}:
          </span>
        </Col>
        <Col span={5}>
          <Input
            onChange={(e) =>
              setInformacijeDozvole({
                postanskiBrojProizvodnje: e.target.value,
              })
            }
            value={stateValues.postanskiBrojProizvodnje}
            name="postanski-broj"
            disabled={isDisabled()}
            className={style.fontSize}
          />
        </Col>
        <Col span={5} className="alignRight">
          <span className={style.fontSize}>
            {isMandatoryIfDisabled(
              "AKCIZNE_DOZVOLE.BROJ_RESENJA_NADLEZNOG_ORGANA_U_POGLEDU_TEHNICKE_OPREMLJENOSTI"
            )}
            :
          </span>
        </Col>
        <Col span={5}>
          <Input
            onChange={(e) =>
              setInformacijeDozvole({
                brojResenja: e.target.value,
              })
            }
            value={stateValues.brojResenja}
            name="broj-resenja"
            maxLength={60}
            disabled={isDisabled()}
            className={style.fontSize}
          />
        </Col>
      </Row>
      <Row className={style.row}>
        <Col span={5} className="alignRight pt-6">
          <span className={style.fontSize}>
            {isMandatoryIfDisabled("AKCIZNE_DOZVOLE.MESTO")}:
          </span>
        </Col>
        <Col span={5}>
          <Input
            onChange={(e) =>
              setInformacijeDozvole({
                mestoProizvodnje: e.target.value,
              })
            }
            value={stateValues.mestoProizvodnje}
            className={style.fontSize}
            disabled={isDisabled()}
            name="mesto"
          />
        </Col>
        <Col span={5} className="alignRight">
          <span className={style.fontSize}>
            {isMandatoryIfDisabled(
              "AKCIZNE_DOZVOLE.DATUM_RESENJA_NADLEZNOG_ORGANA_U_POGLEDU_TEHNICKE_OPREMLJENOSTI"
            )}
            :
          </span>
        </Col>
        <Col span={5}>
          <DatePicker
            format={DATE_FORMAT}
            showToday={false}
            onChange={(value) =>
              setInformacijeDozvole({
                datumResenjaNadleznogOrgana: value,
              })
            }
            //@ts-ignore
            value={
              stateValues.datumResenjaNadleznogOrgana
                ? dayjs(stateValues.datumResenjaNadleznogOrgana)
                : stateValues.datumResenjaNadleznogOrgana
            }
            placeholder={t("PLACEHOLDERS.DATUM_VAZENJA")}
            name="resenje-nadleznog-organa"
            disabled={isDisabled()}
            className={style.fontSize}
          />
        </Col>
      </Row>
      {oilInput()}
    </Col>
  );
};
